import type { SiteCopyPart } from '@wm/domain';
import { Card, Checkbox, Col, DatePicker, Form, Input, Row, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

export interface CompanyValues {
  legalName: string;
  ice?: string;
  rc?: string;
  if?: string;
  address?: string;
  city?: string;
  country: string;
  /** FR-PAR-35: the company carries its own dangerous waste. */
  ownTransport?: boolean;
  ownTransportAuthorization?: {
    number?: string;
    issuingAuthority?: string;
    validity?: [Dayjs, Dayjs];
    notes?: string;
  };
}

export interface SiteValues {
  companyId: string;
  name: string;
  city?: string;
  region?: string;
  address?: string;
  responsableDechets: { name: string; email: string };
  numberingMask: string;
  /** FR-PAR-47: start from the configuration of another site. */
  copyFrom?: { sourceSiteId?: string; parts: SiteCopyPart[] };
}

export const REGIONS = [
  'Tanger-Tétouan-Al Hoceïma',
  'Oriental',
  'Fès-Meknès',
  'Rabat-Salé-Kénitra',
  'Béni Mellal-Khénifra',
  'Casablanca-Settat',
  'Marrakech-Safi',
  'Drâa-Tafilalet',
  'Souss-Massa',
  'Guelmim-Oued Noun',
  'Laâyoune-Sakia El Hamra',
  'Dakhla-Oued Ed-Dahab',
];

export const NEW_SITE_DEFAULTS = {
  name: '',
  numberingMask: '{YEAR}{MONTH}-{AUTOID}',
  responsableDechets: { name: '', email: '' },
};

/** The body of POST/PATCH /companies: optional identifiers left empty are not sent. */
export const companyPayload = (v: CompanyValues) => {
  const { ownTransport, ownTransportAuthorization: a, ...rest } = v;
  return {
    ...rest,
    ice: v.ice || undefined,
    if: v.if || undefined,
    ownTransportAuthorization:
      ownTransport && a?.number && a.validity
        ? {
            number: a.number,
            issuingAuthority: a.issuingAuthority || undefined,
            validFrom: a.validity[0].format('YYYY-MM-DD'),
            validTo: a.validity[1].format('YYYY-MM-DD'),
            notes: a.notes || undefined,
          }
        : null,
  };
};

/** Legal identity of a company (FR-PAR-01), inside a vertical Form. */
export function CompanyFields() {
  const { t } = useTranslation();
  const ownTransport = Form.useWatch('ownTransport');
  return (
    <>
      <Form.Item
        name="legalName"
        label={t('organisation.legalName')}
        rules={[{ required: true, min: 2 }]}
      >
        <Input />
      </Form.Item>
      <Row gutter={12}>
        <Col span={24}>
          <Form.Item
            name="ice"
            label="ICE"
            rules={[{ pattern: /^\d{15}$/, message: t('validation.ice') }]}
            extra={t('organisation.iceHint')}
          >
            <Input maxLength={15} inputMode="numeric" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="rc" label="RC">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="if"
            label="IF"
            rules={[{ pattern: /^\d{7,8}$/, message: t('validation.if') }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="address" label={t('common.address')}>
        <Input />
      </Form.Item>
      <Row gutter={12}>
        <Col xs={24} sm={16}>
          <Form.Item name="city" label={t('common.city')}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="country"
            label={t('common.country')}
            rules={[{ required: true, len: 2 }]}
          >
            <Input maxLength={2} />
          </Form.Item>
        </Col>
      </Row>
      <Card size="small" title={t('organisation.ownTransport.title')}>
        <Form.Item
          name="ownTransport"
          valuePropName="checked"
          extra={t('organisation.ownTransport.hint')}
          style={{ marginBottom: ownTransport ? 12 : 0 }}
        >
          <Checkbox>{t('organisation.ownTransport.enabled')}</Checkbox>
        </Form.Item>
        {ownTransport && (
          <>
            <Row gutter={12}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={['ownTransportAuthorization', 'number']}
                  label={t('organisation.ownTransport.number')}
                  rules={[{ required: true }]}
                >
                  <Input className="wm-mono" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={['ownTransportAuthorization', 'issuingAuthority']}
                  label={t('organisation.ownTransport.authority')}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name={['ownTransportAuthorization', 'validity']}
              label={t('organisation.ownTransport.validity')}
              rules={[{ required: true }]}
            >
              <DatePicker.RangePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name={['ownTransportAuthorization', 'notes']}
              label={t('common.notes')}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea rows={2} />
            </Form.Item>
          </>
        )}
      </Card>
    </>
  );
}

/** A new site with its responsable déchets and numbering mask (FR-PAR-02, FR-PAR-03). */
export function SiteFields() {
  const { t } = useTranslation();
  return (
    <>
      <Form.Item name="companyId" hidden>
        <Input />
      </Form.Item>
      <Form.Item
        name="name"
        label={t('sitePrefs.fields.name')}
        rules={[{ required: true, min: 2 }]}
      >
        <Input placeholder={t('organisation.sitePlaceholder')} />
      </Form.Item>
      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item name="city" label={t('common.city')}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="region" label={t('sitePrefs.fields.region')}>
            <Select allowClear showSearch options={REGIONS.map((r) => ({ value: r, label: r }))} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="address" label={t('common.address')}>
        <Input />
      </Form.Item>
      <div className="wm-section-title">{t('sitePrefs.sections.responsable')}</div>
      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['responsableDechets', 'name']}
            label={t('common.name')}
            rules={[{ required: true, min: 2 }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['responsableDechets', 'email']}
            label={t('common.email')}
            rules={[{ required: true, type: 'email' }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name="numberingMask"
        label={t('sitePrefs.fields.mask')}
        rules={[{ required: true }]}
        extra={t('organisation.maskHint')}
      >
        <Input className="wm-mono" />
      </Form.Item>
    </>
  );
}
